import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import { Button, Badge, Alert, SearchBar, Spinner } from './shared';

const TRANSPORT_BADGE = { http: 'info', sse: 'purple', stdio: 'warning' };

export default function AdminMCP() {
    const navigate = useNavigate();
    const [servers, setServers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => { loadServers(); }, []);

    const loadServers = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminApi.listMCPServers({ include_inactive: true });
            setServers(res.servers || []);
        } catch (err) {
            setError(err.message || 'Failed to load MCP servers');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (e, mcpId) => {
        e.stopPropagation();
        if (!confirm(`Delete MCP server "${mcpId}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteMCPServer(mcpId, true);
            setSuccessMessage(`MCP server "${mcpId}" deleted`);
            loadServers();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleReload = async () => {
        try {
            const result = await adminApi.reloadMCPServers();
            setSuccessMessage(`Reloaded ${result.reloaded} servers, ${result.tools_reloaded || 0} tools`);
            loadServers();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSeed = async () => {
        try {
            const result = await adminApi.seedMCPServers();
            setSuccessMessage(`Seeded: ${result.seeded?.length || 0}, Skipped: ${result.skipped?.length || 0}`);
            loadServers();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const filteredServers = servers.filter(s => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return s.id.toLowerCase().includes(q) || s.name.toLowerCase().includes(q) || (s.description && s.description.toLowerCase().includes(q));
    });

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">MCP Servers</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {servers.length} server{servers.length !== 1 ? 's' : ''} — Model Context Protocol servers that provide tools to agents
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={handleSeed}>Seed Built-in</Button>
                    <Button size="sm" onClick={() => navigate('/admin/mcp/new')}>Add Server</Button>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <SearchBar value={searchQuery} onChange={setSearchQuery} onSearch={loadServers} onRefresh={handleReload} placeholder="Search MCP servers..." className="mb-4" />

            {loading ? (
                <Spinner size="lg" text="Loading MCP servers..." center />
            ) : filteredServers.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No MCP servers found. Click "Seed Built-in" to add defaults.</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Server</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Transport</th>
                                <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Owner</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-28">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filteredServers.map((server) => (
                                <tr key={server.id} className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/mcp/${server.id}`)}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {server.icon_url && <img src={server.icon_url} alt="" className="w-5 h-5 rounded" onError={(e) => { e.target.style.display = 'none'; }} />}
                                            <span className="font-medium text-gray-900 truncate max-w-[200px]">{server.name}</span>
                                            {server.is_builtin && <Badge variant="success">Built-in</Badge>}
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">
                                            {server.server_url || server.command || server.description || '-'}
                                        </p>
                                    </td>
                                    <td className="px-3 py-3"><Badge variant="outline">{server.id}</Badge></td>
                                    <td className="px-3 py-3">
                                        <Badge variant={TRANSPORT_BADGE[server.transport_type] || 'default'}>
                                            {server.transport_type?.toUpperCase()}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3 text-center">
                                        <Badge variant={server.is_active ? 'success' : 'danger'} dot>
                                            {server.is_active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className="text-xs text-gray-600">{server.owner_type}</span>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="xs" onClick={() => navigate(`/admin/mcp/${server.id}/edit`)}>Edit</Button>
                                            <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, server.id)}>Delete</Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
